import classnames from 'classnames2';
import React from 'react';

import { ITestProps } from '../../utils/dataTest';

import { Button } from '../../Button';
import { Description } from '../../Description';
import { Key } from '../../Key';
import { Text } from '../../Text';

import styles from './AccordionWizard.Step.module.scss';

export interface IAccordionWizardStepProps extends ITestProps {
  /** Renders the step as active in the Accordion. */
  isActive?: boolean;
  /** Only the next button is visible. */
  isFirstStep?: boolean;
  /** Checkmark is visible. */
  isComplete?: boolean;
  /** A back button and submit button are visible. */
  isLastStep?: boolean;
  /** Whether or not the next/submit button is disabled. */
  buttonDisabled?: boolean;
  children?: any;
  /** The position of the step within the AccordionWizard. */
  index?: number;
  /** The text label for the step. */
  label?: string;
  /** The text label for the step Key component. */
  keyLabel: string;
  /** Text for all back buttons */
  backButtonText?: string;
  /** Text for all next buttons */
  nextButtonText?: string;
  /** Text for the Submit button */
  submitButtonText?: string;
  summary?: React.ReactNode;
  /** Callback function for handling the back button click event. */
  onBack?(): void;
  /** Callback function for handling the next button click event. */
  onNext?(): void;
  /** Callback function for handling the submit button click event. */
  onSubmit?(): void;
}

export const AccordionWizardStepDefaultProps: Partial<IAccordionWizardStepProps> = {
  isActive: false,
  isFirstStep: false,
  isComplete: false,
  isLastStep: false,
  buttonDisabled: true,
  backButtonText: 'Back',
  index: 0,
  nextButtonText: 'Next',
  submitButtonText: 'Create'
};

export const AccordionWizardStep: React.SFC<IAccordionWizardStepProps> = ({
  buttonDisabled,
  children,
  dataTest,
  keyLabel,
  label,
  isFirstStep,
  isComplete,
  isActive,
  isLastStep,
  backButtonText,
  nextButtonText,
  submitButtonText,
  summary,
  onBack,
  onNext,
  onSubmit
}) => {
  const stepStyles = classnames(styles.step, {
    [styles.isActive]: isActive,
    [styles.isLastStep]: isLastStep
  });

  return (
    <li className={stepStyles} role="listitem" data-test={dataTest}>
      <div className={styles.block}>
        <div className={styles.top}>
          <Key complete={isComplete} text={keyLabel} variant={isActive ? 'active' : 'inactive'} />
          <div className={styles.info}>
            {isActive ? (
              <div>
                <Text>{label}</Text>
              </div>
            ) : (
              <div>
                <Text color="light">{label}</Text>
              </div>
            )}
            {summary && !isActive && (typeof summary === 'string' ? <Description>{summary}</Description> : summary)}
          </div>
        </div>
        {isActive && (
          <>
            <div className={styles.content}>{children}</div>
            <div className={styles.buttons}>
              {!isFirstStep && (
                <Button variant="neutral" dataTest="back-button" onClick={onBack}>
                  {backButtonText}
                </Button>
              )}
              {isLastStep ? (
                <Button variant="primary" dataTest="submit-button" disabled={buttonDisabled} onClick={onSubmit}>
                  {submitButtonText}
                </Button>
              ) : (
                <Button variant="primary" dataTest="next-button" disabled={buttonDisabled} onClick={onNext}>
                  {nextButtonText}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </li>
  );
};

AccordionWizardStep.defaultProps = AccordionWizardStepDefaultProps;
